import type { Dispatch } from "react";
import type { EditorAction } from "../../editor/reducers";
import { useState } from "react";
import { TextFormField } from "../../attributes/text/TextFormField";
import { RegionUpdateForm } from "../RegionUpdateForm";
import { assertTypeName } from "../../client/guards";
import { CaptionFormField } from "../../attributes/caption/CaptionFormField";
import { useRichTextEditor } from "../../rich-text/useRichTextEditor";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";
import { Underline } from "@tiptap/extension-underline";
import { Language } from "../../rich-text/extension-language";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  useCaptionRegionEditorSuspenseQuery,
  useUpdateCaptionRegionMutation,
} from "../../client/generated";

type Props = {
  captionAttributeId: string;
  textAttributeId: string;
  dispatch: Dispatch<EditorAction>;
};

export function CaptionRegionEditor({
  captionAttributeId,
  textAttributeId,
  dispatch,
}: Props) {
  const { data } = useCaptionRegionEditorSuspenseQuery({
    variables: {
      captionAttributeId,
      textAttributeId,
    },
  });

  const { captionAttr, textAttr } = data;
  assertTypeName(captionAttr?.__typename === "CaptionAttribute");
  assertTypeName(textAttr?.__typename === "TextAttribute");

  const [updateRegion] = useUpdateCaptionRegionMutation();
  const [linkedRegionId, setLinkedRegionId] = useState(
    captionAttr.linkedRegion?.id,
  );

  const editor = useRichTextEditor({
    options: {
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        Strike,
        Underline,
        Language,
        TextStyle,
        Color,
      ],
      content: textAttr.text,
    },
  });

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!editor) return false;

    const result = await updateRegion({
      variables: {
        captionAttributeId,
        textAttributeId,
        linkedRegionId,
        text: editor.getHTML(),
      },
    });

    return (
      !result.errors &&
      !result.data?.updateCaptionAttribute.userErrors?.length &&
      !result.data?.updateTextAttribute.userErrors?.length
    );
  };

  return (
    <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch}>
      <CaptionFormField
        fragment={captionAttr.region}
        value={linkedRegionId ?? ""}
        onValueChange={setLinkedRegionId}
      />
      <TextFormField fragment={textAttr.region} editor={editor} />
    </RegionUpdateForm>
  );
}
